import { createSlice, current } from "@reduxjs/toolkit";
import update from "react-addons-update";
import { FormattedMessage } from "react-intl";
import { openSnackbar } from "store/slices/snackbar";
import axios from "utils/axios";
import { getErrorCode, openSnackbarWithError } from "utils/errors";
import { dispatch } from "../index";

const initialState = {
  error: null,
  isLoadingStats: true,
  notifications: {
    data: [],
    isLoading: false,
  },
  detailedStats: {
    isLoading: true,
    data: null,
    hasError: false,
  },
  photograph: {
    isLoading: true,
    data: null,
    hasError: false,
  },
  hasStatsError: false,
  publicProfile: {
    data: null,
    error: null,
    isLoading: true,
    isPosting: false,
  },
  orders: {
    data: [],
    error: null,
    isLoading: false,
  },
  order: {
    data: null,
    error: null,
    isLoading: true,
    isPostingShipment: false,
  },
  purchases: {
    isLoading: false,
    error: null,
    data: [],
  },
};

const slice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    getNotifcations(state, action) {
      state.notifications.isLoading = true;
    },

    getNotifcationsSuccess(state, action) {
      state.notifications.data = action.payload;
      state.notifications.isLoading = false;
    },

    getNotifcationsFailure(state, action) {
      state.notifications.data = [];
      state.notifications.isLoading = false;
      state.notifications.error = action.payload;
    },

    readAllNotifications(state, action) {
      state.notifications.isReadingAllPosting = true;
    },

    readAllNotificationsSuccess(state, action) {
      state.notifications.isReadingAllPosting = false;
      state.notifications.data = state.notifications.data.map((notif) => ({
        ...notif,
        read: true,
      }));
    },

    getPublicProfile(state, action) {
      state.publicProfile.isLoading = true;
    },

    getPublicProfileSuccess(state, action) {
      state.publicProfile.data = action.payload;
      state.publicProfile.isLoading = false;
      state.publicProfile.error = null;
    },

    getPublicProfileFailure(state, action) {
      state.publicProfile.data = null;
      state.publicProfile.isLoading = false;
      state.publicProfile.error = action.payload;
    },

    putPublicProfile(state, action) {
      state.publicProfile.isPosting = true;
    },

    putPublicProfileSuccess(state, action) {
      state.publicProfile = update(state.publicProfile, {
        data: { $set: action.payload },
      });
      state.publicProfile.isPosting = false;
    },

    putPublicProfileFailure(state, action) {
      state.publicProfile.isPosting = false;
      state.publicProfile.error = action.payload;
    },

    fetchDetailedStats(state, action) {
      state.isLoadingStats = true;
    },

    fetchDetailedStatsSuccess(state, action) {
      state.detailedStats.data = action.payload;
      // state.detailedStats.data.moneyEarnByMonths = Object.fromEntries(
      //     Object.entries( state.detailedStats.data.moneyEarnByMonths)
      //     .map(([key, value]) => [key, value / 100])
      // );
      state.detailedStats.isLoading = false;
      state.detailedStats.hasError = false;
    },

    fetchDetailedStatsFailure(state, action) {
      state.detailedStats.data = null;
      state.detailedStats.isLoading = false;
      state.detailedStats.hasError = true;
    },

    fetchPhotograph(state, action) {
      state.photograph.isLoading = true;
    },
    fetchPhotographSuccess(state, action) {
      state.photograph.data = action.payload;
      state.photograph.isLoading = false;
      state.photograph.hasError = false;
    },
    fetchPhotographFailure(state, action) {
      state.photograph.data = null;
      state.photograph.isLoading = false;
      state.photograph.hasError = true;
    },

    decrementSuperAthleteCount(state) {
      state.photograph.data.super_athletes_count =
        state.photograph.data.super_athletes_count - 1;
    },

    getOrders(state, action) {
      state.orders.isLoading = true;
    },
    getOrdersSuccess(state, action) {
      state.orders.data = action.payload;
      state.orders.isLoading = false;
    },
    getOrdersFailure(state, action) {
      state.orders.data = [];
      state.orders.isLoading = false;
      state.orders.error = action.payload;
    },
    getOrder(state, action) {
      state.order.isLoading = true;
    },
    getOrderSuccess(state, action) {
      state.order.data = action.payload;
      state.order.isLoading = false;
    },
    getOrderFailure(state, action) {
      state.order.data = null;
      state.order.isLoading = false;
      state.order.error = action.payload;
    },
    postShipment(state, action) {
      state.order.isPostingShipment = true;
    },
    postShipmentSuccess(state, action) {
      state.order.isPostingShipment = false;
      state.order.data = action.payload;
    },
    postShipmentFailure(state, action) {
      state.order.isPostingShipment = false;
      state.order.errorPostingShipment = action.payload;
    },
    addPhotographNbImportedPhotos(state, action) {
      const addedPhotos = action.payload;
      state.photograph.data.nb_imported_photos =
        current(state).photograph.data.nb_imported_photos + addedPhotos;
    },
    addTotalLimit(state, action) {
      const addedLimit = action.payload;
      state.photograph.data.total_limit =
        current(state).photograph.data.total_limit + addedLimit;
    },
    fetchPurchase(state) {
      state.purchases.isLoading = true;
      state.purchases.error = null;
    },

    fetchPurchaseOk(state, action) {
      state.purchases.isLoading = false;
      state.purchases.data = action.payload;
      state.purchases.error = null;
    },
    fetchPurchaseError(state, action) {
      state.purchases.isLoading = false;
      state.purchases.data = [];
      state.purchases.error = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function fetchDetailedStats() {
  return async () => {
    try {
      dispatch(slice.actions.fetchDetailedStats());
      const response = await axios.get("/api/v1/detailed-stats");
      dispatch(slice.actions.fetchDetailedStatsSuccess(response.data));
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.fetchDetailedStatsFailure());
    }
  };
}

export function fetchPhotograph() {
  return async () => {
    try {
      dispatch(slice.actions.fetchPhotograph());
      const response = await axios.get("/api/v1/me");
      dispatch(slice.actions.fetchPhotographSuccess(response.data));
    } catch (error) {
      console.log("error in fetchPhotograph");
      console.log(error);
      dispatch(slice.actions.fetchPhotographFailure());
    }
  };
}

export function getPublicProfile() {
  return async () => {
    try {
      dispatch(slice.actions.getPublicProfile());
      const response = await axios.get("/api/v1/public-profile");
      console.log(response);
      dispatch(slice.actions.getPublicProfileSuccess(response.data));
    } catch (error) {
      console.log(error);
      openSnackbarWithError(error, dispatch);
      dispatch(slice.actions.getPublicProfileFailure());
    }
  };
}

export function putPublicProfile(data) {
  return async () => {
    try {
      dispatch(slice.actions.putPublicProfile());
      const response = await axios.put("/api/v1/public-profile", data);
      dispatch(slice.actions.putPublicProfileSuccess(response.data));
      dispatch(
        openSnackbar({
          open: true,
          message: "Profil mis à jour avec succès",
          variant: "default",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      openSnackbarWithError(error, dispatch);
      dispatch(slice.actions.putPublicProfileFailure(error));
    }
  };
}

export function getOrders() {
  return async () => {
    try {
      dispatch(slice.actions.getOrders());
      const response = await axios.get("/api/v1/orders");
      dispatch(slice.actions.getOrdersSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getOrdersFailure(error));
    }
  };
}

export function getOrder(id) {
  return async () => {
    try {
      dispatch(slice.actions.getOrder());
      const response = await axios.get(`/api/v1/orders/${id}`);
      dispatch(slice.actions.getOrderSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getOrderFailure(error));
    }
  };
}

export function postShipment(orderId, shipmentTrackingNumber) {
  return async () => {
    try {
      dispatch(slice.actions.postShipment());
      const response = await axios.post(`/api/v1/orders/${orderId}/shipped`, {
        shipment_tracking_number: shipmentTrackingNumber,
      });
      dispatch(slice.actions.postShipmentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.postShipmentFailure(error.message));
    }
  };
}

export function addPhotographNbImportedPhotos(nbPhotosAdded) {
  return async () => {
    dispatch(slice.actions.addPhotographNbImportedPhotos(nbPhotosAdded));
  };
}

export function decrementSuperAthleteCount(nbAthletes) {
  return async () => {
    dispatch(slice.actions.decrementSuperAthleteCount(nbAthletes));
  };
}

export function purchase(credits) {
  return async () => {
    try {
      await axios.post(`/api/v1/purchase`, {
        credits,
      });
      dispatch(slice.actions.addTotalLimit(credits));
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="profile.purchase.purchased" />,
          variant: "default",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      const errorCode = getErrorCode(error);
      dispatch(
        openSnackbar({
          open: true,
          message: errorCode,
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
    }
  };
}

export function getPurchasesHistory() {
  return async () => {
    try {
      dispatch(slice.actions.fetchPurchase());
      const response = await axios.get(`/api/v1/purchases`);
      dispatch(slice.actions.fetchPurchaseOk(response.data));
    } catch (error) {
      dispatch(slice.actions.fetchPurchaseError(error));
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="errors.unknown" />,
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
    }
  };
}

export function getNotifications() {
  return async () => {
    try {
      dispatch(slice.actions.getNotifcations());
      const response = await axios.get("/api/v1/notifications");
      dispatch(slice.actions.getNotifcationsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getNotifcationsFailure(error));
    }
  };
}

export function readAllNotifications(id) {
  return async () => {
    try {
      dispatch(slice.actions.readAllNotifications());
      await axios.post(`/api/v1/notifications/read-all`);
      dispatch(slice.actions.readAllNotificationsSuccess());
    } catch (error) {
      console.log(error);
    }
  };
}
